import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useRole from '../../hooks/use-role';
import { cn } from '../../utils/cn';
import { useOrganizationsDictStore } from '../organizations/state/use-organizations-dict-store';
import ChatPane from './components/ChatPane';
import ThreadFilters from './components/ThreadFilters';
import ThreadList from './components/ThreadList';
import { ATTACHMENT_REFRESH_MS, MESSAGES_POLL_MS, THREADS_POLL_MS } from './constants';
import { useSupportStore } from './state/use-support-store';
import useSupportAccess from './use-support-access';
import useVisibleInterval from './use-visible-interval';

const SupportContent = () => {
  const { canRead, canWrite, engineerId } = useSupportAccess();
  const { isMeridianOrg, isEngineer } = useRole();
  const [params, setParams] = useSearchParams();
  const threadFromUrl = params.get('thread');

  const { filters, page, selectedId, threadError, threadsError, fetchThreads, pollThreads, pollMessages, refreshAttachmentUrls, openThread, setEngineerId, loadEngineers } = useSupportStore();
  const { fetchOrganizations } = useOrganizationsDictStore();

  useEffect(() => {
    if (!canRead) return;
    setEngineerId(engineerId);
    fetchThreads();
  }, [canRead, engineerId, filters, page, fetchThreads, setEngineerId]);

  useEffect(() => {
    if (!canRead || isEngineer) return;
    loadEngineers();
  }, [canRead, isEngineer, loadEngineers]);

  useEffect(() => {
    if (!canRead || !isMeridianOrg) return;
    fetchOrganizations();
  }, [canRead, isMeridianOrg, fetchOrganizations]);

  useEffect(() => {
    if (!canRead) return;

    const id = Number(threadFromUrl);
    if (!threadFromUrl) {
      if (useSupportStore.getState().selectedId) openThread(null);
      return;
    }

    if (!id) return;
    const currentError = useSupportStore.getState().threadError;
    if (currentError?.status === 404 && String(currentError.threadId) === String(id)) return;
    if (useSupportStore.getState().selectedId === id && useSupportStore.getState().thread) return;

    openThread(id);
  }, [canRead, threadFromUrl, openThread]);

  useEffect(() => {
    if (threadError?.status !== 404 || String(threadError.threadId) !== String(threadFromUrl)) return;
    const next = new URLSearchParams(params);
    next.delete('thread');
    setParams(next, { replace: true });
  }, [threadError, threadFromUrl, params, setParams]);

  useVisibleInterval(pollThreads, THREADS_POLL_MS, canRead);
  useVisibleInterval(pollMessages, MESSAGES_POLL_MS, canRead && Boolean(selectedId));
  useVisibleInterval(refreshAttachmentUrls, ATTACHMENT_REFRESH_MS, canRead && Boolean(selectedId));

  const selectThread = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set('thread', String(id));
    else next.delete('thread');
    setParams(next);
  };

  if (!canRead || threadsError?.status === 403) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <p className="text-lg text-gray-500">Нет доступа</p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100dvh-3rem)] overflow-hidden border-l border-gray-300 bg-[#F7F7F8] md:h-screen">
      <section
        className={cn(
          'flex min-h-0 w-full flex-col border-r border-gray-300 bg-white md:w-[340px] md:shrink-0',
          selectedId && 'hidden md:flex'
        )}>
        <ThreadFilters />
        <ThreadList onSelect={selectThread} />
      </section>

      <section className={cn('flex min-h-0 min-w-0 flex-1 bg-[#F7F7F8]', !selectedId && 'hidden md:flex')}>
        <ChatPane onBack={() => selectThread(null)} canWrite={canWrite} />
      </section>
    </div>
  );
};

export default SupportContent;
